import { z } from 'zod'
import {
  TRAINER_CONTRACT_VERSION,
  trainerReadSideEffectsSchema,
} from './trainer-event-read'

export const TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerTrainingCentreReadQuerySchema = z.object({
  orgId: optionalQueryString,
  includeArchived: optionalQueryString,
  include_archived: optionalQueryString,
  status: optionalQueryString,
  providerMode: optionalQueryString,
  limit: optionalQueryString,
}).passthrough()

export const trainerTrainingCentreReadSideEffectsSchema = trainerReadSideEffectsSchema.passthrough()

export const trainerTrainingCentreSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  name: z.string().nullable(),
  venueName: z.string(),
  location: z.string(),
  eventName: z.string(),
  requestedStartDate: z.string().nullable(),
  requestedEndDate: z.string().nullable(),
  eventDesc: z.string().nullable(),
  additionalIns: z.string().nullable(),
  noParticipants: z.number().finite().nullable(),
  courseType: z.string().nullable(),
  budgets: z.string().nullable(),
  companyName: z.string().nullable(),
  contactPersonName: z.string(),
  contactPersonMobileNo: z.string().nullable(),
  contactPersonEmail: z.string(),
  requirements: z.string().nullable(),
  requestStatus: z.string(),
  assignedToUserId: z.string().nullable(),
  assignedByUserId: z.string().nullable(),
  assignedAt: z.string().nullable(),
  convertedEventId: z.string().nullable(),
  convertedByUserId: z.string().nullable(),
  convertedAt: z.string().nullable(),
  idempotencyKey: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerTrainingCentreReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('trainer_training_centre')]),
  projection: z.literal('trainer-training-centre-catalog'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerTrainingCentreNotesEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_training_centre'),
    z.literal('trainer_training_centre_notes'),
  ]),
  projection: z.literal('trainer-training-centre-notes'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerTrainingCentreAssigneeEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('user_profiles'),
    z.literal('org_user'),
    z.literal('product_role_grants'),
    z.literal('role_definitions'),
  ]),
  projection: z.literal('trainer-training-centre-active-assignees'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerTrainingCentreListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION),
  trainingCentres: z.array(trainerTrainingCentreSummarySchema),
  trainingCentreEvidence: trainerTrainingCentreReadEvidenceSchema,
  sideEffects: trainerTrainingCentreReadSideEffectsSchema,
}).passthrough()

export const trainerTrainingCentreDetailResponseSchema = z.object({
  contract_version: z.literal(TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION),
  trainingCentre: trainerTrainingCentreSummarySchema,
  trainingCentreEvidence: trainerTrainingCentreReadEvidenceSchema,
  sideEffects: trainerTrainingCentreReadSideEffectsSchema,
}).passthrough()

export const trainerTrainingCentreNoteSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  trainingCentreId: z.string().min(1),
  createdByUserId: z.string().min(1),
  noteText: z.string(),
  noteType: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerTrainingCentreNotesResponseSchema = z.object({
  contract_version: z.literal(TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION),
  notes: z.array(trainerTrainingCentreNoteSummarySchema),
  trainingCentreNotesEvidence: trainerTrainingCentreNotesEvidenceSchema,
  sideEffects: trainerTrainingCentreReadSideEffectsSchema,
}).passthrough()

export const trainerTrainingCentreAssigneeSchema = z.object({
  userId: z.string().min(1),
  displayName: z.string(),
  role: z.string().nullable(),
  roleKey: z.string().nullable(),
}).passthrough()

export const trainerTrainingCentreAssigneesResponseSchema = z.object({
  contract_version: z.literal(TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION),
  assignees: z.array(trainerTrainingCentreAssigneeSchema),
  assigneeEvidence: trainerTrainingCentreAssigneeEvidenceSchema,
  sideEffects: trainerTrainingCentreReadSideEffectsSchema,
}).passthrough()

const outboxProviderModeSchema = z.enum(['dry_run', 'live'])
const outboxStatusSchema = z.enum(['pending', 'claimed', 'succeeded', 'dead_letter', 'dry_run_completed'])

export const trainerTrainingCentreExternalNotificationOutboxRowSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  sourceEntityType: z.string(),
  sourceEntityId: z.string(),
  provider: z.string(),
  actionType: z.string(),
  status: z.string(),
  providerMode: outboxProviderModeSchema,
  idempotencyKey: z.string(),
  payloadHash: z.string(),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().nonnegative(),
  nextAttemptAt: z.string().nullable(),
  providerAuditId: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorSummary: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
}).passthrough()

export const trainerTrainingCentreExternalNotificationOutboxExportSchema = z.object({
  surface: z.literal('trainer_training_centre_external_notification_outbox'),
  generatedAt: z.string(),
  format: z.literal('json_redacted'),
  rowCount: z.number().int().nonnegative(),
  filters: z.object({
    orgId: z.number().int().positive().nullable(),
    status: outboxStatusSchema.nullable(),
    providerMode: outboxProviderModeSchema.nullable(),
    limit: z.number().int().positive(),
  }).passthrough(),
  byProvider: z.record(z.number().int().nonnegative()),
  byStatus: z.record(z.number().int().nonnegative()),
  byProviderMode: z.record(z.number().int().nonnegative()),
  providerTouched: z.literal(false),
  rawPayloadReturned: z.literal(false),
  rawContactReturned: z.literal(false),
  rawEmailReturned: z.literal(false),
  secretReturned: z.literal(false),
  liveDeliveryBlocked: z.literal(true),
  blockingGate: z.string(),
}).passthrough()

export const trainerTrainingCentreExternalNotificationOutboxResponseSchema = z.object({
  contract_version: z.literal(TRAINER_TRAINING_CENTRE_READ_CONTRACT_VERSION),
  export: trainerTrainingCentreExternalNotificationOutboxExportSchema,
  rows: z.array(trainerTrainingCentreExternalNotificationOutboxRowSchema),
}).passthrough()

export type TrainerTrainingCentreReadQuery = z.infer<typeof trainerTrainingCentreReadQuerySchema>
export type TrainerTrainingCentreReadSideEffects = z.infer<typeof trainerTrainingCentreReadSideEffectsSchema>
export type TrainerTrainingCentreSummary = z.infer<typeof trainerTrainingCentreSummarySchema>
export type TrainerTrainingCentreReadEvidence = z.infer<typeof trainerTrainingCentreReadEvidenceSchema>
export type TrainerTrainingCentreListResponse = z.infer<typeof trainerTrainingCentreListResponseSchema>
export type TrainerTrainingCentreDetailResponse = z.infer<typeof trainerTrainingCentreDetailResponseSchema>
export type TrainerTrainingCentreNoteSummary = z.infer<typeof trainerTrainingCentreNoteSummarySchema>
export type TrainerTrainingCentreNotesResponse = z.infer<typeof trainerTrainingCentreNotesResponseSchema>
export type TrainerTrainingCentreAssignee = z.infer<typeof trainerTrainingCentreAssigneeSchema>
export type TrainerTrainingCentreAssigneesResponse = z.infer<typeof trainerTrainingCentreAssigneesResponseSchema>
export type TrainerTrainingCentreExternalNotificationOutboxRow = z.infer<typeof trainerTrainingCentreExternalNotificationOutboxRowSchema>
export type TrainerTrainingCentreExternalNotificationOutboxExport = z.infer<typeof trainerTrainingCentreExternalNotificationOutboxExportSchema>
export type TrainerTrainingCentreExternalNotificationOutboxResponse = z.infer<typeof trainerTrainingCentreExternalNotificationOutboxResponseSchema>
